import { StatusBadge } from '../../../../components/base/StatusBadge';
import { formatDate, formatVND, formatNumber } from '../../../../utils/format';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import type { Harvest, Contract } from '../../../../types';

interface HarvestDetailModalProps {
  harvest: Harvest;
  farmerName: string;
  seasonName: string;
  fieldName: string;
  contractWarning?: string;
  linkedContract?: Contract;
  onClose: () => void;
  onConfirm?: () => void;
}

export default function HarvestDetailModal({
  harvest, farmerName, seasonName, fieldName,
  contractWarning, linkedContract, onClose, onConfirm,
}: HarvestDetailModalProps) {
  useModalBackHandler(true, onClose);
  // Against this harvest's own "Dự kiến" shown just above — same record, same
  // unit. See the note in HarvestCard for why the season's yield cannot be used.
  const duKien = harvest.soLuong || 0;
  const thucTe = harvest.actualYieldConfirmed || harvest.actualYield || 0;
  const chenhLech = thucTe - duKien;
  const showConfirm = harvest.status === 'Nông hộ đã cập nhật' || harvest.status === 'Chờ HTX xác nhận';

  const getCanhBaoColor = (canhBao?: string) => {
    if (!canhBao) return '';
    if (canhBao === 'Sản lượng thấp') return 'bg-red-50 text-red-700 border-red-200';
    if (canhBao === 'Thu hoạch trễ') return 'bg-orange-50 text-orange-700 border-orange-200';
    if (canhBao === 'Mùa vụ có nguy cơ giảm sản lượng') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (canhBao === 'Ảnh hưởng đến hợp đồng') return 'bg-sky-50 text-sky-700 border-sky-200';
    return 'bg-amber-50 text-amber-700 border-amber-200';
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-2xl p-6 overflow-y-auto max-h-[100dvh] sm:max-h-[90vh]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Chi tiết lần thu hoạch</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Cảnh báo banner */}
        {harvest.canhBao && (
          <div className={`mb-4 p-3 rounded-xl border flex items-center gap-2 ${getCanhBaoColor(harvest.canhBao)}`}>
            <span className="w-5 h-5 flex items-center justify-center shrink-0"><i className="ri-alert-line" /></span>
            <p className="text-sm font-medium">{harvest.canhBao}</p>
          </div>
        )}
        {contractWarning && (
          <div className="mb-4 p-3 rounded-xl border bg-sky-50 text-sky-700 border-sky-200 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center shrink-0"><i className="ri-file-list-3-line" /></span>
            <p className="text-sm font-medium">{contractWarning}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Mã + trạng thái */}
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-900">{harvest.maLanThuHoach}</p>
            <StatusBadge status={harvest.status} />
          </div>

          {/* Thông tin cơ bản */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <p className="text-sm font-medium text-slate-700 mb-2">Thông tin chung</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <p className="text-slate-600"><span className="text-slate-400">Nông hộ:</span> {farmerName}</p>
              <p className="text-slate-600"><span className="text-slate-400">Mùa vụ:</span> {seasonName}</p>
              <p className="text-slate-600"><span className="text-slate-400">Thửa đất:</span> {fieldName}</p>
              <p className="text-slate-600"><span className="text-slate-400">Cây trồng:</span> {harvest.cayTrong}</p>
              <p className="text-slate-600"><span className="text-slate-400">Loại sản phẩm:</span> {harvest.loaiSanPham}</p>
              <p className="text-slate-600"><span className="text-slate-400">Giá ước tính:</span> {formatVND(harvest.giaUocTinh)}/{harvest.donVi}</p>
            </div>
          </div>

          {/* Sản lượng */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <p className="text-sm font-medium text-slate-700 mb-2">Sản lượng</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <p className="text-slate-600"><span className="text-slate-400">Dự kiến:</span> {formatNumber(harvest.soLuong)} {harvest.donVi}</p>
              <p className="text-slate-600"><span className="text-slate-400">Nông hộ cập nhật:</span> {harvest.actualYield ? `${formatNumber(harvest.actualYield)} ${harvest.donVi}` : 'Chưa cập nhật'}</p>
              <p className="text-slate-600"><span className="text-slate-400">HTX xác nhận:</span> {harvest.actualYieldConfirmed ? `${formatNumber(harvest.actualYieldConfirmed)} ${harvest.donVi}` : 'Chờ xác nhận'}</p>
              {duKien > 0 && thucTe > 0 && (
                <p className="text-slate-600">
                  <span className="text-slate-400">Chênh lệch:</span>
                  <span className={`font-medium ml-1 ${chenhLech >= 0 ? 'text-agrida-700' : 'text-red-600'}`}>
                    {chenhLech >= 0 ? '+' : ''}{formatNumber(chenhLech)} {harvest.donVi}
                  </span>
                  {' '}({((thucTe / duKien) * 100).toFixed(1)}%)
                </p>
              )}
            </div>
          </div>

          {/* Đối chiếu với hợp đồng bao tiêu liên quan */}
          {linkedContract && (
            <div className="bg-sky-50 rounded-xl p-4 space-y-2 border border-sky-100">
              <p className="text-sm font-medium text-sky-800 mb-2">Đối chiếu với hợp đồng {linkedContract.soHopDong}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <p className="text-slate-600">
                  <span className="text-slate-400">Sản lượng cam kết:</span>{' '}
                  {linkedContract.sanLuongDuKien ? `${formatNumber(linkedContract.sanLuongDuKien)} kg` : 'Chưa có'}
                  {linkedContract.sanLuongDuKien && thucTe > 0 && (
                    <span className={`ml-1 font-medium ${thucTe >= linkedContract.sanLuongDuKien ? 'text-agrida-700' : 'text-red-600'}`}>
                      ({thucTe >= linkedContract.sanLuongDuKien ? '+' : ''}{formatNumber(thucTe - linkedContract.sanLuongDuKien)} kg)
                    </span>
                  )}
                </p>
                <p className="text-slate-600">
                  <span className="text-slate-400">Đơn giá dự kiến:</span>{' '}
                  {linkedContract.donGiaDuKien ? `${formatVND(linkedContract.donGiaDuKien)}/kg` : 'Chưa có'}
                  {linkedContract.donGiaDuKien && harvest.giaUocTinh > 0 && (
                    <span className={`ml-1 font-medium ${harvest.giaUocTinh >= linkedContract.donGiaDuKien ? 'text-agrida-700' : 'text-red-600'}`}>
                      (thực tế {formatVND(harvest.giaUocTinh)}/{harvest.donVi})
                    </span>
                  )}
                </p>
                <p className="text-slate-600 sm:col-span-2">
                  <span className="text-slate-400">Ngày giao hàng dự kiến:</span>{' '}
                  {linkedContract.ngayGiaoHangDuKien ? formatDate(linkedContract.ngayGiaoHangDuKien) : 'Chưa có'}
                  {linkedContract.ngayGiaoHangDuKien && harvest.ngayThuHoach && (
                    <span className="ml-1 font-medium text-slate-500">(thực tế {formatDate(harvest.ngayThuHoach)})</span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Ngày */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <p className="text-sm font-medium text-slate-700 mb-2">Thời gian</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <p className="text-slate-600"><span className="text-slate-400">Ngày dự kiến:</span> {harvest.ngayDuKien ? formatDate(harvest.ngayDuKien) : '-'}</p>
              <p className="text-slate-600"><span className="text-slate-400">Ngày thực tế:</span> {harvest.ngayThuHoach ? formatDate(harvest.ngayThuHoach) : '-'}</p>
            </div>
          </div>

          {/* Chất lượng + ghi chú */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <p className="text-sm font-medium text-slate-700 mb-2">Chất lượng & Ghi chú</p>
            <div className="space-y-1 text-sm">
              {harvest.chatLuong && <p className="text-slate-600"><span className="text-slate-400">Chất lượng:</span> {harvest.chatLuong}</p>}
              {harvest.ghiChuChatLuong && <p className="text-slate-600"><span className="text-slate-400">Ghi chú chất lượng:</span> {harvest.ghiChuChatLuong}</p>}
              {harvest.ghiChu && <p className="text-slate-600"><span className="text-slate-400">Ghi chú:</span> {harvest.ghiChu}</p>}
            </div>
          </div>

          {/* Lịch sử cập nhật */}
          {harvest.lichSuCapNhat && harvest.lichSuCapNhat.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium text-slate-700 mb-2">Lịch sử cập nhật</p>
              <div className="space-y-2">
                {harvest.lichSuCapNhat.map((ls, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0 text-slate-400 mt-0.5">
                      <i className="ri-time-line" />
                    </span>
                    <div>
                      <p className="text-slate-600">{ls.noiDung}</p>
                      <p className="text-xs text-slate-400">{ls.nguoiCapNhat} · {formatDate(ls.thoiGian)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ảnh */}
          {harvest.hinhAnhList && harvest.hinhAnhList.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium text-slate-700 mb-2">Ảnh đính kèm</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {harvest.hinhAnhList.map((img, idx) => (
                  <div key={idx} className="aspect-square rounded-xl overflow-hidden bg-slate-100">
                    <img src={img} alt={`Ảnh thu hoạch ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
          >
            Đóng
          </button>
          {showConfirm && onConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              className="px-4 py-2.5 rounded-md text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 transition-colors whitespace-nowrap min-h-[44px]"
            >
              Xác nhận thu hoạch
            </button>
          )}
        </div>
      </div>
    </div>
  );
}